import { groupBy, orderBy } from "lodash-es";
import moment from "moment";
import { captureException } from "extensions/sentry";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";

import { rate } from "common/formats";
import { store } from "common/store";

import { getters } from "common/components/getters/getters";
import { references } from "common/helpers/references";
import { PazienError } from "common/helpers/errors";
import { walk } from "common/components/utils/walk";
import { getRate } from "common/helpers/accountingHelper";

export function normalizeSeriesData(data, schema) {
    return schema.map(computeSingleSeria(data));
}

const IconMarker = styled.div(({ $color, $shape }) => `
        width: 10px;
        height: 10px;
        display: inline-block;
        background-color: ${$color};
        border: 0;
        border-radius: ${$shape === "square" ? "0" : "50%"};
    `);

const SeriaRow = function({
    name, color, metricValue, percentOfTotal, showPercentOfTotal, bulletIcon
}) {
    return (
        <div key={name} className="tooltip-row">
            <div className="color-indicator cell">
                <IconMarker $color={color} $shape={bulletIcon} />
            </div>
            <div className="pivot-value cell">{name}</div>
            <div className="metric cell">{metricValue}</div>
            {showPercentOfTotal && <div className="metric-percent-of-total cell">{Number.isFinite(percentOfTotal) ? rate(percentOfTotal) : ""}</div> }
        </div>
    );
};

export function jsxSharedTooltip({
    header,
    points,
    width = "auto",
    showPercentOfTotal = true,
    useSeriesNameIfOne = true,
    bulletIcon = "circle"
}) {
    const grouppedPoints = groupBy(points, (p) => p.series.options.pivot);
    
    return ReactDOMServer.renderToStaticMarkup(
        <div className="tooltip-custom-shared" style={{ width }}>
            {header && <div className="tooltip-title">{header}</div>}
            {orderBy(Object.entries(grouppedPoints), ([pivot, pivotPoints]) => pivotPoints.length).map(([pivot, pivotPoints]) => {
                if (pivotPoints.length === 1) {
                    return (
                        <div className="values">
                            <div className="body">
                                {pivotPoints.map((point, index) => (
                                    <SeriaRow
                                        key={index++}
                                        bulletIcon={bulletIcon}
                                        name={useSeriesNameIfOne ? point.series.name : references.toAlias(point.point.name, point.series.options.pivot)}
                                        color={useSeriesNameIfOne ? point.series.color : point.point.color}
                                        metricValue={point.series.options.dataFormatter(point.y)}
                                        percentOfTotal={point.series.options.pivot === "date_time" ? null : 100}
                                        showPercentOfTotal={showPercentOfTotal}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                }
                
                const total = pivotPoints.reduce((sum, point) => (point.y || 0) + sum, 0);
                
                return (
                    <div className="values">
                        <div className="header">
                            <div className="color-indicator cell"/>
                            <div className="pivot-value cell">{getters.findPivotName(pivot)}</div>
                            <div className="metric cell">{pivotPoints[0].point.series.options.getter_name}</div>
                            {showPercentOfTotal && <div className="metric-percent-of-total cell">% of total</div>}
                        </div>
                        <div className="body">
                            {pivotPoints.map((point, index) => (
                                <SeriaRow
                                    key={index++}
                                    bulletIcon={bulletIcon}
                                    name={references.toAlias(point.series.name, point.series.options.pivot)}
                                    color={point.series.color}
                                    metricValue={point.series.options.dataFormatter(point.y)}
                                    showPercentOfTotal={showPercentOfTotal}
                                    percentOfTotal={showPercentOfTotal ? getRate({
                                        value: point.y,
                                        total,
                                        precision: 2
                                    }) : void 0}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export function jsxPointTooltip({
    color,
    formatter,
    key,
    point,
    series,
    y
}) {
    return ReactDOMServer.renderToStaticMarkup(
        <div className="tooltip-custom-point">
            <div className="color-bullet">
                <IconMarker $color={color} $shape="square" />
            </div>
            <div className="tooltip-data">
                <div className="tooltip-title">
                    {key}<br />{moment.utc(series.name, "MMM YY").format("MMMM YYYY")}
                </div>
                <div className="tooltip-metric">{series.yAxis.userOptions.title?.title || series.yAxis.userOptions.title}</div>
                <div className="values">{formatter(point)(y)}</div>
            </div>
        </div>
    );
}

export function computeSingleSeria(seriaData) {
    return ({
        pointFormat,
        valueType,
        sorted,
        seriaPointer,
        ...extend
    }) => {
        const obj = {
            ...extend
        };
        
        if (pointFormat) {
            let data = seriaData;
            
            if (typeof seriaPointer !== "undefined") { // seriaPointer can be 0
                if (seriaData[seriaPointer]) {
                    data = seriaData[seriaPointer];
                } else {
                    throw new Error(`Missing seriaPointer in seriaData object or seriaData is not an object: ${seriaPointer}`);
                }
            }
    
            obj.data = data.map(computeSeriaPoint({ pointFormat }));
        }
        
        if (valueType === "amount") {
            const state = store.getState().common;
            obj.tooltip = {
                valueSuffix: ` ${state.currency}`,
                valueDecimals: state.currency_decimals
            };
        } else if (valueType === "rate") {
            obj.tooltip = {
                valueSuffix: "%",
                valueDecimals: 2
            };
        }
        
        // Highcharts like sorted points! Sort points by first item
        if (!sorted) {
            obj.data = obj.data.sort(([a], [b]) => a - b);
        }
        
        return obj;
    };
}

export function computeSeriaPoint({ pointFormat }) {
    return (dataPoint) => {
        if (typeof pointFormat === "function") {
            return pointFormat(dataPoint);
        }
        
        return pointFormat.map((seriaPoint) => {
            if (typeof seriaPoint === "function") {
                return seriaPoint.call(this, dataPoint);
            }
    
            if (seriaPoint === "date_time") {
                try {
                    return moment.utc(dataPoint.date_time).valueOf();
                } catch {
                    captureException(new Error("Cannot convert date_time to moment instance"));
                }
            }
    
            return walk(dataPoint, seriaPoint);
        });
    };
}

export const plotOptions = {
    column: {
        pointPadding: 0,
        groupPadding: 0
    },
    area: {
        fillOpacity: 0.2,
        marker: {
            enabledThreshold: 5,
            lineWidth: 2,
            fillColor: "#ffffff",
            lineColor: null,
            symbol: "circle",
            radius: 3
        },
        lineWidth: 3,
        connectNulls: true
    },
    line: {
        marker: {
            enabledThreshold: 5,
            lineWidth: 2,
            fillColor: "#ffffff",
            lineColor: null,
            symbol: "circle",
            radius: 3
        },
        lineWidth: 3,
        connectNulls: true
    }
};

export function getTickInterval(interval = "day") {
    const ms = moment.duration(1, interval).asMilliseconds();
    return {
        tickInterval: ms,
        minRange: ms,
        minTickInterval: ms
    };
}

export function isoDateToInterval(isoDate) {
    if (moment(isoDate, "YYYY-[Q]Q", true).isValid()) {
        return "quarter";
    }
    
    if (moment(isoDate, "YYYY-[W]WW", true).isValid()) {
        return "week";
    }
    
    if (moment(isoDate, "YYYY-MM", true).isValid()) {
        return "month";
    }
    
    if (moment(isoDate, "YYYY-MM-DD", true).isValid()) {
        return "day";
    }
    
    throw new PazienError({
        message: "Unknown interval",
        extra: {
            isoDate
        }
    });
}

export function getIntervalFromDates(startDate, endDate) {
    return ["month", "quarter", "year"].find((interval) => {
        const startMoment = moment(startDate);
        const endMoment = moment(endDate);
        
        const isOneInterval = Math.round(endMoment.diff(startMoment, interval, true));
        
        return isOneInterval === 1
            && startMoment.clone().startOf(interval).isSame(startMoment, "minute");
    });
}

export const dateTimeLabelFormats = {
    month: "%b %Y",
    year: "%Y",
    day: "%a %b %e",
    week: "%G"
};
